import { Link, useNavigate, useLocation } from "react-router-dom";
import { Download } from "lucide-react";
import { Logo } from "@/components/Logo";

export function Footer() {
  return (
    <footer className="border-t border-white/10 bg-black" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid gap-12 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div>
            <Logo />
            <p className="mt-5 text-sm text-fog max-w-xs leading-relaxed">
              Technology systems & cybersecurity consulting. Securing digital
              transformation for the organizations that build the future.
            </p>
            <a
              href="/Aliwer_Company_Overview.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-download-overview"
              className="mt-6 group inline-flex items-center gap-2 rounded-full border border-white/20 text-paper text-sm font-medium px-5 py-2.5 hover:bg-white/5 transition-colors"
            >
              <Download size={15} />
              Download overview
            </a>
          </div>
          <FooterCol
            title="Services"
            items={[
              { label: "HIPAA — DFW Practices", to: "/hipaa" },
              { label: "Free Exposure Check", to: "/exposure-check" },
              { label: "Baseline Review ($499)", to: "/baseline-review" },
              { label: "Security Assessment", to: "/services/security-assessment" },
              { label: "Penetration Testing", to: "/services/penetration-testing" },
              { label: "Cloud Security", to: "/services/cloud-security" },
              { label: "All Services", to: "/services" },
            ]}
          />
          <FooterCol
            title="Company"
            items={[
              { label: "About", to: "/company/about" },
              { label: "Our Approach", to: "/approach" },
              { label: "Pricing", to: "/pricing" },
              { label: "The Aliwer Method", to: "/company/methodology" },
              { label: "Solutions", to: "/solutions" },
              { label: "Resources", to: "/resources" },
              { label: "Trust Center", to: "/trust" },
              { label: "Our SOC 2 Journey", to: "/trust/soc2-journey" },
              { label: "Contact", to: "/contact" },
            ]}
          />
          <FooterCol
            title="Platform"
            items={[
              { label: "BArecord", to: "/barecord" },
              { label: "SecureDesk", to: "/products/securedesk" },
              { label: "Assessment Platform", to: "/assessments" },
              { label: "Client Portal", to: "/client" },
              { label: "Partner Portal", to: "/partner" },
              { label: "Become a Partner", to: "/register" },
            ]}
          />
        </div>
        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-fog">© {new Date().getFullYear()} Aliwer. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/trust/security" data-testid="footer-security" className="text-xs text-fog hover:text-paper transition-colors">Security</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="text-xs text-fog hover:text-paper transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="text-xs text-fog hover:text-paper transition-colors">Terms of Service</Link>
            <Link to="/cookies" data-testid="footer-cookies" className="text-xs text-fog hover:text-paper transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (to) => (e) => {
    if (/^https?:\/\//.test(to)) {
      window.open(to, "_blank", "noopener,noreferrer");
      e.preventDefault();
      return;
    }
    e.preventDefault();
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname === "/") {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate(to); // Landing reads the hash on mount and scrolls
      }
    } else {
      navigate(to);
    }
  };

  const testid = (label) => `footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div>
      <h4 className="text-xs uppercase tracking-widest text-paper mb-4">{title}</h4>
      <ul className="space-y-3">
        {items.map((i) => (
          <li key={i.label}>
            <a
              href={i.to}
              onClick={go(i.to)}
              data-testid={testid(i.label)}
              className="text-sm text-fog hover:text-paper transition-colors cursor-pointer"
            >
              {i.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
